import { Button, Popconfirm, Tooltip } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteFilled,
  EditFilled,
  EyeFilled,
  NodeIndexOutlined,
} from "@ant-design/icons";

const headerStyle = { backgroundColor: "#E0F8AD", color: "#006939", fontWeight: "bold" };
const getTypeName = (record) => typeof record.type === "string" ? record.type : record.type?.name;

const Columns = ({
  showEditModal,
  handleDelete,
  handleOpenWorkflow,
  handleViewWorkflow,
  handleDeleteWorkflow,
  filters,
  openingWorkflowCode,
  viewingWorkflowCode,
  deletingWorkflowCode,
}) => [
  {
    title: "Proceso",
    key: "process",
    render: (_, record) => record.asset?.name || "N/A",
    sorter: (a, b) => (a.asset?.name || "").localeCompare(b.asset?.name || ""),
    filters: filters.processes.map((name) => ({ text: name, value: name })),
    onFilter: (value, record) => record.asset?.name === value,
    onHeaderCell: () => ({ style: headerStyle }),
    showSorterTooltip: false,
    width: "19%",
  },
  {
    title: "Subproceso",
    dataIndex: "name",
    key: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
    filters: filters.subprocesses.map((name) => ({ text: name, value: name })),
    onFilter: (value, record) => record.name === value,
    onHeaderCell: () => ({ style: headerStyle }),
    showSorterTooltip: false,
    width: "19%",
  },
  {
    title: "Descripción",
    dataIndex: "description",
    key: "description",
    render: (value) => value || "-",
    sorter: (a, b) => (a.description || "").localeCompare(b.description || ""),
    onHeaderCell: () => ({ style: headerStyle }),
    showSorterTooltip: false,
    width: "25%",
  },
  {
    title: "Responsable",
    key: "area",
    render: (_, record) => record.asset?.area?.name || "N/A",
    filters: filters.areas.map((area) => ({ text: area, value: area })),
    onFilter: (value, record) => record.asset?.area?.name === value,
    onHeaderCell: () => ({ style: headerStyle }),
    width: "13%",
  },
  {
    title: "Tipo",
    key: "type",
    render: (_, record) => getTypeName(record) || "N/A",
    filters: filters.types.map((type) => ({ text: type, value: type })),
    onFilter: (value, record) => getTypeName(record) === value,
    onHeaderCell: () => ({ style: headerStyle }),
    width: "11%",
  },
  {
    title: "Diagrama",
    key: "diagram",
    align: "center",
    render: (_, record) => record.workflows_count > 0
      ? (
          <Tooltip title="Si tiene diagrama">
            <CheckCircleFilled style={{ color: "#63c000", fontSize: 18 }} aria-label="Sí" />
          </Tooltip>
        )
      : (
          <Tooltip title="No tiene diagrama">
            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} aria-label="No" />
          </Tooltip>
        ),
    filters: [
      {
        text: <><CheckCircleFilled style={{ color: "#63c000", marginRight: 6 }} />Sí</>,
        value: true,
      },
      {
        text: <><CloseCircleFilled style={{ color: "#ff4d4f", marginRight: 6 }} />No</>,
        value: false,
      },
    ],
    onFilter: (value, record) => (record.workflows_count > 0) === value,
    onHeaderCell: () => ({ style: { ...headerStyle, textAlign: "center" } }),
    width: "10%",
  },
  {
    title: "Acciones",
    key: "actions",
    onHeaderCell: () => ({
      className: "processes-actions-group-header",
      style: { ...headerStyle, textAlign: "center" },
    }),
    children: [
      {
        title: "Subproceso",
        key: "subprocess-actions",
        align: "center",
        width: 105,
        onHeaderCell: () => ({
          className: "processes-actions-subheader",
          style: { ...headerStyle, textAlign: "center" },
        }),
        render: (_, record) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Button
              type="link"
              style={{ color: "#006939", fontSize: 18, padding: "0px 2px" }}
              onClick={() => showEditModal(record)}
            >
              <Tooltip title="Editar subproceso"><EditFilled /></Tooltip>
            </Button>
            <Popconfirm
              overlayClassName="custom-popconfirm"
              title="¿Seguro que deseas eliminar este Subproceso?"
              onConfirm={() => handleDelete(record.code)}
            >
              <Button type="link" style={{ color: "#006939", fontSize: 18, padding: "0px 2px" }}>
                <Tooltip title="Eliminar subproceso"><DeleteFilled /></Tooltip>
              </Button>
            </Popconfirm>
          </div>
        ),
      },
      {
        title: "Diagrama",
        key: "workflow-actions",
        align: "center",
        width: 155,
        onHeaderCell: () => ({
          className: "processes-actions-subheader",
          style: { ...headerStyle, textAlign: "center" },
        }),
        render: (_, record) => {
          const hasWorkflow = (record.workflows_count || 0) > 0;
          const workflowActionLoading = [
            openingWorkflowCode,
            viewingWorkflowCode,
            deletingWorkflowCode,
          ].includes(record.code);

          return (
            <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
              <Button
                type="link"
                disabled={!hasWorkflow || workflowActionLoading}
                style={{ color: hasWorkflow ? "#006939" : "#999", fontSize: 18, padding: "0px 2px" }}
                onClick={() => handleViewWorkflow(record)}
              >
                <Tooltip title={hasWorkflow ? "Ver diagrama" : "No hay diagrama para visualizar"}><EyeFilled /></Tooltip>
              </Button>
              <Button
                type="link"
                disabled={workflowActionLoading}
                style={{ color: "#006939", fontSize: 18, padding: "0px 2px" }}
                onClick={() => handleOpenWorkflow(record)}
              >
                <Tooltip title={hasWorkflow ? "Abrir diagrama" : "Crear diagrama"}><NodeIndexOutlined /></Tooltip>
              </Button>
              <Popconfirm
                overlayClassName="custom-popconfirm"
                title="¿Seguro que deseas eliminar el diagrama?"
                description="Se eliminará el diagrama de flujo, pero el subproceso permanecerá disponible."
                onConfirm={() => handleDeleteWorkflow(record)}
                disabled={!hasWorkflow}
              >
                <Button
                  type="link"
                  disabled={!hasWorkflow || workflowActionLoading}
                  style={{ color: hasWorkflow ? "#006939" : "#999", fontSize: 18, padding: "0px 2px" }}
                >
                  <Tooltip title={hasWorkflow ? "Eliminar diagrama" : "No hay diagrama para eliminar"}><DeleteFilled /></Tooltip>
                </Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ],
  },
];

export default Columns;
